import React, { useState } from 'react';
import { Box, Chip, Collapse, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { CombinedBoolean, CombinedNumeric, CombinedText, QuestionInsight } from '../insightsTypes';
import NumberBadge from '../shared/NumberBadge';
import CategoryOverview from './CategoryOverview';
import QuestionAccordionRow from '../question/QuestionAccordionRow';

const AWAITING_INSIGHTS_TOOLTIP =
    'These questions have no analyzed calls yet. They are analyzed on new calls, or use Re-run analysis to generate them.';

interface QuestionCategorySectionProps {
    index: number;
    title: string;
    questions: QuestionInsight[];
    /** Server-computed rollup for this section's type. */
    combined: CombinedNumeric | CombinedBoolean | CombinedText | null;
}

const QuestionCategorySection: React.FC<QuestionCategorySectionProps> = ({ index, title, questions, combined }) => {
    const [expanded, setExpanded] = useState(true);
    const toggle = () => setExpanded((prev) => !prev);

    // Questions that have no analyzed answers yet — surfaced in the header so the
    // gap is visible without expanding every row.
    const awaitingCount = questions.filter((question) => question.overall.responseCount === 0).length;
    return (
        <Paper
            elevation={0}
            sx={{
                mb: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '14px',
                boxShadow: '0 1px 2px rgba(16, 22, 51, 0.04), 0 6px 20px rgba(16, 22, 51, 0.05)',
            }}
        >
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
                sx={{
                    width: '100%',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '-2px',
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        px: '20px',
                        py: '18px',
                        width: '100%',
                        gap: 1.5,
                    }}
                >
                    <NumberBadge value={index} />

                    <Typography
                        sx={{
                            width: '200px',
                            minWidth: 0,
                            flexShrink: 1,
                            fontSize: '16px',
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {title}
                    </Typography>

                    <Chip
                        size="small"
                        label={`${questions.length} ${questions.length === 1 ? 'question' : 'questions'}`}
                        sx={{
                            height: 22,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'gray.100',
                            bgcolor: 'gray.30',
                            flexShrink: 0,
                            '& .MuiChip-label': { px: '9px' },
                        }}
                    />

                    <Typography
                        sx={{
                            fontSize: 12.5,
                            color: 'text.disabled',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}
                    >
                        Combined overview
                    </Typography>

                    <Box
                        sx={{
                            ml: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        {awaitingCount > 0 && (
                            <Tooltip arrow title={AWAITING_INSIGHTS_TOOLTIP}>
                                <Chip
                                    size="small"
                                    label={`${awaitingCount} awaiting insights`}
                                    sx={(theme) => ({
                                        height: 22,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: theme.palette.warning.dark,
                                        bgcolor: alpha(theme.palette.warning.main, 0.14),
                                        flexShrink: 0,
                                        '& .MuiChip-label': { px: '9px' },
                                    })}
                                />
                            </Tooltip>
                        )}

                        <Box sx={{ display: 'flex', color: 'text.disabled', flexShrink: 0 }}>
                            {expanded ? <ExpandLess sx={{ fontSize: 22 }} /> : <ExpandMore sx={{ fontSize: 22 }} />}
                        </Box>
                    </Box>
                </Stack>
            </Box>

            <Collapse in={expanded}>
                <CategoryOverview questions={questions} combined={combined} />

                <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    {questions.map((question, position) => (
                        <QuestionAccordionRow key={question.questionId} insight={question} number={position + 1} />
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default QuestionCategorySection;
